/**
 * 복사(빌드) 페이지까지 따라가는 유입 출처와 시도 식별자.
 *
 * `/duplicate/[id]` 와 저장 버튼은 튜토리얼 복사·상세 복사도 같이 쓴다. 어느 흐름에서
 * 왔는지는 **URL 로 나른다** — `enter-referrer-tracking` 의 localStorage 는 TTL 5분이라
 * 회원가입 4페이지를 넘기면 사라지고, 이미 `build_entry_point` 가 통째로 `unknown` 으로
 * 기록된 전례가 있다(`buildEntryPointStore.ts` 주석).
 *
 * 같은 판단을 체험 이용권이 먼저 했다 — `resolveTrialDestination` 의 `trialSource` 가
 * 인증 왕복을 쿼리로 넘긴다("localStorage 대신 query 를 쓰는 이유는 새로고침·뒤로가기에도
 * 재현되고, 앱 전역에 남아 엉뚱한 때 열리는 일이 없기 때문이다"). 그 패턴을 따른다.
 */
export const QUIZ_COPY_SOURCE_QUERY_KEY = 'copySource';

/**
 * 공유 클릭 한 번을 끝까지 잇는 **시도 단위** 식별자.
 *
 * `copySource` 는 "어느 경로"만 말하고 "어느 시도"는 구분하지 못한다. 같은 사람이 두
 * 퀴즈에서 각각 공유를 눌렀거나 한 퀴즈에서 두 번 눌렀을 때 시도가 뭉개지면, 시도 기준
 * 비율과 24시간 귀속을 계산할 수 없다.
 */
export const QUIZ_SHARE_FLOW_QUERY_KEY = 'shareFlowId';

/** 체험 제한 화면에서 온 복사를 체험 한 번(`trial_session_id`)에 잇는 식별자. */
export const QUIZ_TRIAL_SESSION_QUERY_KEY = 'trialSessionId';

/** 알려진 출처. 목록 밖 값은 지표에 섞이지 않게 버린다. */
export type QuizCopySource = 'guest_share' | 'trial_gate';

/**
 * 쿼리에서 읽은 값을 알려진 출처로만 좁힌다.
 *
 * URL 은 사용자가 직접 고칠 수 있다. 허용 목록이 없으면 임의 문자열이 그대로 지표에
 * 쌓여 세그먼트가 오염된다(`normalizeTrialSource` 와 같은 이유).
 */
export const parseQuizCopySource = (
  value: unknown,
): QuizCopySource | undefined => {
  if (value === 'guest_share' || value === 'trial_gate') return value;
  return undefined;
};

/** uuid v4 길이·문자만 통과시킨다. 출처와 달리 값 자체는 자유라 형태로만 거른다. */
const UUID_PATTERN = /^[0-9a-f-]{36}$/i;

const parseUuid = (value: unknown): string | undefined =>
  typeof value === 'string' && UUID_PATTERN.test(value) ? value : undefined;

export const parseShareFlowId = parseUuid;
export const parseTrialSessionId = parseUuid;

/** 복사 경로가 나르는 출처·시도 식별자 묶음. 각 값은 검증을 통과했을 때만 존재한다. */
export type QuizCopyContext = {
  copySource?: QuizCopySource;
  shareFlowId?: string;
  trialSessionId?: string;
};

/** 라우터 쿼리에서 출처·시도 ID 를 한 번에 꺼낸다. */
export const parseQuizCopyContext = (
  query: Record<string, unknown>,
): QuizCopyContext => ({
  copySource: parseQuizCopySource(query[QUIZ_COPY_SOURCE_QUERY_KEY]),
  shareFlowId: parseShareFlowId(query[QUIZ_SHARE_FLOW_QUERY_KEY]),
  trialSessionId: parseTrialSessionId(query[QUIZ_TRIAL_SESSION_QUERY_KEY]),
});

/**
 * 복사 경로에 실어 보낼 쿼리 문자열. 값이 없으면 빈 문자열이라 기존 URL 그대로다.
 */
export const buildQuizCopyQuery = ({
  copySource,
  shareFlowId,
  trialSessionId,
}: {
  copySource?: QuizCopySource;
  shareFlowId?: string;
  trialSessionId?: string;
}) => {
  const params = new URLSearchParams();
  if (copySource) params.set(QUIZ_COPY_SOURCE_QUERY_KEY, copySource);
  if (shareFlowId) params.set(QUIZ_SHARE_FLOW_QUERY_KEY, shareFlowId);
  if (trialSessionId) params.set(QUIZ_TRIAL_SESSION_QUERY_KEY, trialSessionId);

  const query = params.toString();
  return query ? `?${query}` : '';
};

/**
 * `callbackUrl` 안에 감싸인 복사 경로에서 출처·시도 ID 를 꺼낸다.
 *
 * 인증 페이지는 목적지를 통째로 인코딩된 문자열로 받는다. 라우터 쿼리에는 안 보이므로
 * 여기서 한 번 풀어야 가입·로그인 완료를 이 전환에 귀속할 수 있다.
 *
 * 절대 던지지 않는다. callbackUrl 은 주소창 쿼리라 망가진 값(`//[bad`, 잘못된 포트 등)이
 * 들어올 수 있고, 이 함수는 로그인·가입 완료 처리 한가운데서 불린다. 계측이 인증 흐름을
 * 멈추면 안 되므로 파싱에 실패하면 귀속 없음으로 본다 — 목적지 판정(`isSameDomain`)이
 * 같은 입력을 기본 목적지로 폴백하는 것과 같은 태도다.
 */
export const parseQuizCopyContextFromCallbackUrl = (
  callbackUrl: unknown,
): QuizCopyContext => {
  if (typeof callbackUrl !== 'string') return {};

  try {
    // 상대 경로라 파싱용 base 가 필요하다. 값만 읽고 버리므로 base 는 의미 없다.
    const query = new URL(callbackUrl, 'https://quiz.zep.us').searchParams;
    return {
      copySource: parseQuizCopySource(query.get(QUIZ_COPY_SOURCE_QUERY_KEY)),
      shareFlowId: parseShareFlowId(query.get(QUIZ_SHARE_FLOW_QUERY_KEY)),
      trialSessionId: parseTrialSessionId(
        query.get(QUIZ_TRIAL_SESSION_QUERY_KEY),
      ),
    };
  } catch {
    return {};
  }
};
